import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {take} from 'rxjs/operators';

import {LookupService} from '../../../../common/services/lookup.service';
import {SharedService} from '../../../../common/services/shared-service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {LimitDTO} from '../../../models/LimitDTO';
import {LimitService} from '../../../services/limit.service';


@Component({
  selector: 'app-add-limit',
  templateUrl: './add-limit-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_add-view-edit-styles.scss']
})
export class AddLimitDialogComponent implements OnInit {
  readonly lookupValue = LookupValue;

  errorMessage = '';
  heading = 'Add Limit';
  limitFormGroup: FormGroup;
  isEditMode: boolean;
  limitTypeLookupValues: NameValueDTO[];
  measureLookupValues: NameValueDTO[];
  accumulateAtLookupValues: NameValueDTO[];
  accumulateByLookupValues: NameValueDTO[];

  private limitDTO: LimitDTO;
  private loggedInUserName: string;


  constructor(@Inject(MAT_DIALOG_DATA) public data: { limitId: bigint },
              private dialog: MatDialog,
              private dialogRef: MatDialogRef<AddLimitDialogComponent>,
              private limitService: LimitService,
              private formBuilder: FormBuilder,
              private lookupService: LookupService,
              private sharedService: SharedService) {

    this.loggedInUserName = this.sharedService.getLoggedInUserName();
  }

  ngOnInit(): void {
    this.initLookUpValues();
    this.initLimitFormControls();
    this.getHelpInfo('Limit');


    const {limitId} = this.data;
    if (limitId) {
      this.isEditMode = true;
      this.heading = 'Edit Limit';
      this.getLimitByLimitId(limitId);
    }
  }

  initLimitFormControls() {
    this.limitFormGroup = this.formBuilder.group({
      limitId: new FormControl(),
      name: new FormControl(null, [Validators.required]),
      limitType: new FormControl(null, [Validators.required]),
      measure: new FormControl(null, [Validators.required]),
      accumulateBy: new FormControl(null, [Validators.required]),
      accumulateAt: new FormControl(null, [Validators.required]),
      value: new FormControl(null, [Validators.required]),
      startDate: new FormControl(null, [Validators.required]),
      endDate: new FormControl(null)
    });
  }

  initLookUpValues() {
    this.getListOfLookupValues(this.lookupValue.LimitType);
    this.getListOfLookupValues(this.lookupValue.LimitMeasure);
    this.getListOfLookupValues(this.lookupValue.AccumulateBy);
    this.getListOfLookupValues(this.lookupValue.AccumulateAt);
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }


  onSaveLimit() {
    const limitDTOPayload = this.getLimitDTOPayload();
    this.limitService.saveLimit(limitDTOPayload)
      .pipe(
        take(1)
      )
      .subscribe((response: LimitDTO | any) => {
          if (response) {
            if (this.isEditMode) {
              this.sharedService.openSnackBar(`Limit edited successfully`, 'Dismiss');
            }
            else {
              this.sharedService.openSnackBar(`Limit added successfully`, 'Dismiss');
            }

            this.dialogRef.close({
              isSaved: true
            });

            this.resetState();
          }
          else if (response && response.businessMessage) {
            this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
          }
        },
        (error) => {
          console.error('Failed to save limit', error);
          this.sharedService.openErrorDialog(error.message);
        });
  }

  private getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity)
      .pipe(
        take(1)
      )
      .subscribe((response: NameValueDTO[]) => {
          if (entity === this.lookupValue.LimitType) {
            this.limitTypeLookupValues = response;
          }
          else if (entity === this.lookupValue.LimitMeasure) {
            this.measureLookupValues = response;
          }
          else if (entity === this.lookupValue.AccumulateAt) {
            this.accumulateAtLookupValues = response;
          }
          else if (entity === this.lookupValue.AccumulateBy) {
            this.accumulateByLookupValues = response;
          }
        },
        errorResponse => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        });
  }

  private getLimitByLimitId(limitId: bigint): void {
    this.limitService.getLimitByLimitId(limitId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response: LimitDTO) => {
          if (response) {
            this.limitDTO = response;
            this.setFormsForEdit(response);
          }
          else {
            this.sharedService.openSnackBar(`Limit by ${limitId} not found`, 'Dismiss');
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private setFormsForEdit(data: LimitDTO) {
    this.setLimitFormForEdit(data);
  }

  private setLimitFormForEdit(limit: LimitDTO) {
    const {limitId, name, measure, accumulateBy, accumulateAt, limitType, value, startDate, endDate} = limit;

    this.heading = `Edit Limit - ${name}`;
    this.limitFormGroup.patchValue({
      limitId,
      name,
      measure: measure.value,
      limitType: limitType.value,
      accumulateBy: accumulateBy.value,
      accumulateAt: accumulateAt.value,
      value,
      startDate: new Date(startDate),
      endDate: endDate ? new Date(endDate) : null
    });
  }

  private getLimitDTOPayload() {
    let payload: LimitDTO;

    // Get Selected Dropdown Value -> Names to Find `value` based on Lookup value
    const measure = this.limitFormGroup.get('measure').value;
    const accumulateBy = this.limitFormGroup.get('accumulateBy').value;
    const accumulateAt = this.limitFormGroup.get('accumulateAt').value;
    const limitType = this.limitFormGroup.get('limitType').value;

    const endDate = this.limitFormGroup.get('endDate').value;

    payload = {
      limitId: this.isEditMode ? this.limitDTO.limitId : null,
      name: this.limitFormGroup.get('name').value,
      value: this.limitFormGroup.get('value').value,
      startDate: this.limitFormGroup.get('startDate').value,
      endDate: endDate ? new Date(endDate) : null, // Avoid sending "1970-01-01" in case of endDate is "null"
      measure: {value: measure, name: this.measureLookupValues.find(type => type.value === measure)?.name},
      accumulateBy: {value: accumulateBy, name: this.accumulateByLookupValues.find(type => type.value === accumulateBy).name},
      accumulateAt: {value: accumulateAt, name: this.accumulateAtLookupValues.find(type => type.value === accumulateAt).name},
      limitType: {value: limitType, name: this.limitTypeLookupValues.find(type => type.value === limitType).name},
      created: this.isEditMode ? this.limitDTO.created : new Date(),
      createdBy: this.isEditMode ? this.limitDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.limitDTO.tenantId : null,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null
    };

    return payload;
  }

  private resetState() {
    this.limitDTO = null;

    // Reset FormGroup/Control values:
    this.limitFormGroup.reset();
  }
}
